import Rollbar from 'rollbar';

// Initialize Rollbar configuration
const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
  environment: process.env.NODE_ENV || 'development',

  // Rollbar configuration options
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    client: {
      javascript: {
        code_version: process.env.REACT_APP_VERSION || 'v2',
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },

  // Transform function to add context to errors
  transform: function (payload) {
    // Add user context if available
    const userToken = localStorage.getItem('token');
    if (userToken) {
      try {
        // Add minimal user context (avoid sensitive data)
        payload.person = {
          id: 'authenticated_user',
        };
      } catch {
        // Ignore JWT decode errors
      }
    } else {
      payload.person = {
        id: 'anonymous_user',
      };
    }

    // Add app-specific context
    payload.custom = {
      ...payload.custom,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
  },

  // Filter function to avoid logging sensitive data
  checkIgnore: function (isUncaught, args, payload) {
    // Don't log in development unless explicitly enabled
    if (
      process.env.NODE_ENV === 'development' &&
      !process.env.REACT_APP_ROLLBAR_IN_DEV
    ) {
      return true;
    }

    // Filter out known non-critical errors
    const message =
      payload.body?.message || payload.body?.trace?.exception?.message || '';

    // Filter out common browser extension errors
    if (
      message.includes('Non-Error promise rejection captured') ||
      message.includes('Script error') ||
      message.includes('ResizeObserver loop limit exceeded')
    ) {
      return true;
    }

    return false;
  },
};

// Initialize Rollbar only if we have an access token
let rollbar = null;

// Check if we're running in a browser extension environment
const isExtensionEnvironment =
  // eslint-disable-next-line no-undef
  typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;

if (rollbarConfig.accessToken) {
  try {
    rollbar = new Rollbar(rollbarConfig);
  } catch (error) {
    console.warn('Failed to initialize Rollbar:', error);
  }
} else {
  // Keep tests quiet when Rollbar is intentionally not configured there.
  if (!isExtensionEnvironment && process.env.NODE_ENV !== 'test') {
    console.warn('Rollbar access token not found. Error tracking is disabled.');
  }
}

// Helper functions for logging different types of errors
export const rollbarService = {
  // Log general errors
  error: (error, extra = {}) => {
    if (rollbar) {
      rollbar.error(error, extra);
    } else {
      console.error('Rollbar not initialized:', error, extra);
    }
  },

  // Log API errors specifically
  apiError: (error, requestDetails = {}) => {
    if (rollbar) {
      const errorData = {
        ...requestDetails,
        errorType: 'API_ERROR',
        status: error.status,
        url: requestDetails.url || 'unknown',
        method: requestDetails.method || 'unknown',
      };

      rollbar.error(error, errorData);
    } else {
      console.error(
        'API Error (Rollbar not initialized):',
        error,
        requestDetails
      );
    }
  },

  // Log React component errors
  componentError: (error, errorInfo, componentName = 'Unknown') => {
    if (rollbar) {
      const errorData = {
        errorType: 'REACT_ERROR',
        componentName,
        componentStack: errorInfo?.componentStack,
        errorBoundary: true,
      };

      rollbar.error(error, errorData);
    } else {
      console.error('React Error (Rollbar not initialized):', error, errorInfo);
    }
  },

  // Log warnings
  warning: (message, extra = {}) => {
    if (rollbar) {
      rollbar.warning(message, extra);
    } else {
      console.warn('Rollbar Warning (not initialized):', message, extra);
    }
  },

  // Log info messages
  info: (message, extra = {}) => {
    if (rollbar) {
      rollbar.info(message, extra);
    } else {
      // eslint-disable-next-line no-console
      console.info('Rollbar Info (not initialized):', message, extra);
    }
  },
};

export default rollbar;
